import type {
  Account,
  GlucoseEvent as GlucoseUserEvent,
  GlucoseSubEntry,
  Entry as UserEntry,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import { useMemo } from "react"
import type { DeepReadonlyObject } from "rxdb"
import { useFindUserEntries } from "src/hooks/userEntry"
import { useFindUserEvents } from "src/hooks/userEvent"
import type { Reading } from "src/types"
import {
  createEntryQueryObjectForType,
  createEventQueryObjectForType,
  glucoseEventToReading,
  glucoseSubEntryToReading,
} from "src/utils"

/*
 * find the first glucose reading before the given time or in general
 * if no time is provided
 */
export function useFirstGlucoseReading(
  accountId: Account["id"],
  time?: string,
): Reading | undefined {
  const previousGlucoseEventQuery = useMemo(
    () => createEventQueryObjectForType(
      accountId,
      ActivityType.Glucose,
      {
        selector: (
          time
            ? { started_at: { $lt: time } }
            : {}
        ),
        sort: [{ started_at: "desc" as const }],
        limit: 1,
      },
    ),
    [
      time,
      accountId,
    ],
  )

  const previousGlucoseEntryQuery = useMemo(
    () => createEntryQueryObjectForType(
      accountId,
      ActivityType.Glucose,
      {
        selector: (
          time
            ? { started_at: { $lt: time } }
            : {}
        ),
        sort: [{ started_at: "desc" as const }],
        limit: 1,
      },
    ),
    [
      time,
      accountId,
    ],
  )

  const {
    result: userEvents,
  } = useFindUserEvents<GlucoseUserEvent>(previousGlucoseEventQuery)

  const {
    result: userEntries,
  } = useFindUserEntries(previousGlucoseEntryQuery)

  return useMemo(
    () => {
      const userEvent = userEvents[0]
      const userEntry = userEntries[0]

      if (userEvent && userEntry) {
        if (dayjs(userEvent.started_at).isAfter(userEntry.started_at)) {
          return glucoseEventToReading(userEvent)
        }
        return entryToReading(userEntry)

      }

      if (userEvent) {
        return glucoseEventToReading(userEvent)
      }

      if (userEntry) {
        return entryToReading(userEntry)
      }

      return undefined
    },
    [
      userEvents,
      userEntries,
    ],
  )
}

function entryToReading(entry: DeepReadonlyObject<UserEntry>): Reading | undefined {
  const glucoseSubEntry = entry.subentries.find(
    (subEntry) => subEntry.subentry_type === ActivityType.Glucose,
  ) as GlucoseSubEntry | undefined

  if (!glucoseSubEntry) {
    return undefined
  }

  return glucoseSubEntryToReading(
    entry.started_at,
    glucoseSubEntry,
  )
}
